// client/src/components/Reservations/pages/ManageReservationsPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { reservationService } from '../services/reservationService';
import ReservationCard from '../components/ReservationCard';
import ReservationDetailsModal from '../components/ReservationDetailsModal';
import EditReservationModal from '../components/EditReservationModal';
import { formatMoney } from '../utils/money';
import { DateTime } from 'luxon';

const FILTER_OPTIONS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'all', label: 'All' }
];

const STATUS_FILTERS = [
  { key: 'all', label: 'All Status' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Cancelled' }
];

export default function ManageReservationsPage() {
  const { userId } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters and pagination
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Modal state
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null); // { itemId, offeringKind, offeringId } | null
  const [showEditModal, setShowEditModal] = useState(false);

  // Load reservations
  const loadReservations = async (isLoadMore = false) => {
    // If user isn't available yet, bail out and ensure we are not stuck in loading
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      // Only mark loading when we're about to make a request (not on load-more to avoid over-eager UI changes)
      if (!isLoadMore) setLoading(true);
      
      const params = {
        page: isLoadMore ? page + 1 : 1,
        limit: 10,
        time_filter: timeFilter,
        status_filter: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery || undefined
      };

      const response = await reservationService.getUserReservations(userId, params);
      // Debug: log backend payload to investigate missing offering_name/service label
      try {
        console.log('[debug] getUserReservations response', response);
        if (Array.isArray(response.reservations) && response.reservations.length > 0) {
          console.log('[debug] first reservation from backend', response.reservations[0]);
          // log first reservation's items
          console.log('[debug] first reservation items', response.reservations[0].items);
        }
      } catch (e) {
        console.warn('[debug] failed to log reservations response', e);
      }
      
      if (isLoadMore) {
        setReservations(prev => [...prev, ...response.reservations]);
        setPage(prev => prev + 1);
      } else {
        setReservations(response.reservations);
          setPage(response.page || 1);
      }
      
        setHasMore(response.hasMore);
      setError(null);
    } catch (err) {
      console.error('Failed to load reservations:', err);
      setError('Failed to load reservations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load and filter changes
  // Only trigger load when we have a user id - prevents spinner while auth state resolves
  useEffect(() => {
    if (userId) {
      loadReservations();
    } else {
      // make sure we don't remain in loading state while waiting for auth
      setLoading(false);
    }
  }, [userId, timeFilter, statusFilter, searchQuery]);

  // Handle reservation actions
  const handleReservationUpdate = async (reservationId, updates) => {
    try {
      const updatedReservation = await reservationService.updateReservation(reservationId, updates);
      setReservations(prev => 
        prev.map(r => r.id === reservationId ? updatedReservation : r)
      );
      
      if (selectedReservation?.id === reservationId) {
        setSelectedReservation(updatedReservation);
      }
      
      return updatedReservation;
    } catch (err) {
      console.error('Failed to update reservation:', err);
      throw err;
    }
  };

  const handleReservationCancel = async (reservationId, reason) => {
    try {
      // When invoked from details modal, selectedItem holds the precise item
      const itemId = selectedItem?.itemId || (Array.isArray(selectedReservation?.items) ? selectedReservation.items[0]?.id : null);
      if (!itemId) throw new Error('No reservation item selected');
      await reservationService.cancelReservationItem(itemId, { reason });
      await loadReservations(); // Refresh list
      setShowDetailsModal(false);
    } catch (err) {
      console.error('Failed to cancel reservation:', err);
      throw err;
    }
  };

  const handleArchiveItem = async ({ reservation, itemId }) => {
    try {
      // Confirm with the user
      // eslint-disable-next-line no-alert
      const ok = window.confirm('Remove this item from your list? This will not affect the business; it only hides it for you.');
      if (!ok) return;
      await reservationService.archiveReservationItem(itemId, { reason: 'user_removed' });
      // Refresh current list respecting filters
      await loadReservations();
    } catch (err) {
      console.error('Failed to archive reservation item:', err);
      // Optionally show a toast; for now, alert
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Failed to remove item');
    }
  };

  // Build cards; for bundles, show one card (the bundle line) instead of splitting
  const itemCards = useMemo(() => {
    // Use UTC consistently to avoid off-by-hours filtering hiding items
    const now = DateTime.utc();

    const getItemDt = (item, r) => {
      // For bundles, use bundle_start_date from guests_json for sorting
      if (item?.offering_kind === 'bundle') {
        const bStart = item?.guests_json?.bundle_start_date;
        if (bStart) {
          const dt = DateTime.fromISO(`${bStart}T00:00:00`, { zone: 'utc' });
          if (dt.isValid) return dt;
        }
      }
      const iso = item?.start_at || item?.slot_start || item?.start_iso;
      if (iso) {
        const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
        if (dt.isValid) return dt;
      }
      // Fallback to reservation-level date/time
      const resIso = r.start_at || r.start_iso || r.slot_start;
      if (resIso) {
        const dt = DateTime.fromISO(String(resIso), { zone: 'utc' });
        if (dt.isValid) return dt;
      }
      if (r.date && (item?.time || r.time)) {
        const timeStr = item?.time || r.time;
        const dt = DateTime.fromISO(`${r.date}T${timeStr}`, { zone: 'utc' });
        if (dt.isValid) return dt;
      }
      return null;
    };

    // Build flat list of cards
    const cards = [];
    for (const r of reservations) {
      const items = Array.isArray(r.items) && r.items.length ? r.items : [];
      // In the 'All' tab, do not show placeholder cards for reservations with no items
      if (timeFilter === 'all' && items.length === 0) {
        continue;
      }
      const bundleItem = items.find(x => x?.offering_kind === 'bundle');
      if (bundleItem) {
        const dt = getItemDt(bundleItem, r);
        if (!dt && timeFilter === 'upcoming') continue;
        if (timeFilter === 'upcoming' && dt && dt <= now) continue;
        if (timeFilter === 'past' && dt && dt > now) continue;
        const key = `${r.id || 'res'}_bundle_${bundleItem.id || '0'}`;
        cards.push({ key, reservation: { ...r, items: [bundleItem] }, itemDt: dt });
        continue;
      }
      // Non-bundle: keep per-item cards
      if (items.length === 0) {
        // fallback single reservation-level card
        const dt = getItemDt({}, r);
        if (!dt && timeFilter === 'upcoming') continue;
        if (timeFilter === 'upcoming' && dt && dt <= now) continue;
        if (timeFilter === 'past' && dt && dt > now) continue;
        cards.push({ key: `${r.id || 'res'}_0`, reservation: r, itemDt: dt });
        continue;
      }
      for (let i = 0; i < items.length; i++) {
        const it = items[i] || {};
        const dt = getItemDt(it, r);
        if (!dt && timeFilter === 'upcoming') continue;
        if (timeFilter === 'upcoming' && dt && dt <= now) continue;
        if (timeFilter === 'past' && dt && dt > now) continue;
        const cardReservation = { ...r, items: [it] };
        const key = `${r.id || 'res'}_${it?.id || i}`;
        cards.push({ key, reservation: cardReservation, itemDt: dt });
      }
    }

    // Sort cards by item datetime (soonest first for upcoming; reverse for past)
    cards.sort((a, b) => {
      const da = a.itemDt;
      const db = b.itemDt;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return timeFilter === 'past' ? (db - da) : (da - db);
    });

    return cards;
  }, [reservations, timeFilter]);

  const openDetailsModal = (reservation, itemSel = null) => {
    setSelectedReservation(reservation);
    setSelectedItem(itemSel);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setSelectedReservation(null);
    setSelectedItem(null);
    setShowDetailsModal(false);
    setShowEditModal(false);
  };

  const openEditModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowEditModal(true);
  };

  if (loading && reservations.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your reservations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Reservations</h1>
          <p className="text-gray-600">Manage and view your bookings</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Time Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Period
              </label>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                {FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => setTimeFilter(option.key)}
                    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                      timeFilter === option.key
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div> 
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search by business or service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-1 text-sm text-red-700">{error}</div>
                <div className="mt-2">
                  <button
                    onClick={() => loadReservations()}
                    className="text-sm font-medium text-red-800 underline hover:text-red-600"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reservations List */}
  {itemCards.length === 0 && !loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1V8a1 1 0 011-1h3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reservations found</h3>
            <p className="text-gray-600 mb-6">
              {timeFilter === 'upcoming' 
                ? "You don't have any upcoming reservations." 
                : timeFilter === 'past'
                ? "You don't have any past reservations."
                : "You don't have any reservations yet."
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {itemCards.map(({ key, reservation }) => (
              <ReservationCard
                key={key}
                reservation={reservation}
                onClick={() => openDetailsModal(reservation)}
                onViewDetails={(r, itemSel) => openDetailsModal(r, itemSel)}
                onArchive={handleArchiveItem}
              />
            ))}
            
            {/* Load More Button */}
            {hasMore && (
              <div className="text-center pt-6">
                <button
                  onClick={() => loadReservations(true)}
                  disabled={loading}
                  className="inline-flex items-center px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More Reservations'
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedReservation && (
          <ReservationDetailsModal
            reservation={selectedReservation}
            isOpen={showDetailsModal}
            onClose={closeDetailsModal}
            onUpdate={handleReservationUpdate}
            onCancel={handleReservationCancel}
            selectedItem={selectedItem}
            onOpenEdit={openEditModal}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedReservation && (
          <EditReservationModal
            reservation={selectedReservation}
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSave={handleReservationUpdate}
          />
        )}
      </div>
    </div>
  );
}